/**
 * Header Menu Rail
 *
 * Two web components for the rail-style mega menu:
 *   <header-menu-rail>         — desktop dropdown with left rail + column-flow panels
 *   <header-menu-rail-mobile>  — mobile drill-down stack drawer
 *
 * Both share this file (one HTTP request for the whole menu system).
 */

/* ============================================================
   <header-menu-rail> — desktop dropdown
   ============================================================ */

const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 150;

class HeaderMenuRail extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-rail-trigger]');
    this.dropdown = this.querySelector('[data-rail-dropdown]');
    this.railItems = Array.from(this.querySelectorAll('[data-rail-target]'));
    this.panels = Array.from(this.querySelectorAll('[data-rail-panel]'));

    if (!this.trigger || !this.dropdown || this.railItems.length === 0) return;

    this.openTimer = null;
    this.closeTimer = null;

    // Open/close the dropdown — scoped to the trigger and dropdown only,
    // so hovering flat sibling links (Brands, Promos, etc.) does NOT open it.
    // Listening on both trigger and dropdown lets the pointer move between
    // them without triggering close.
    this.trigger.addEventListener('pointerenter', this.scheduleOpen);
    this.trigger.addEventListener('pointerleave', this.scheduleClose);
    this.dropdown.addEventListener('pointerenter', this.scheduleOpen);
    this.dropdown.addEventListener('pointerleave', this.scheduleClose);
    this.trigger.addEventListener('focus', this.open);
    this.trigger.addEventListener('click', this.toggle);
    this.addEventListener('focusout', this.handleFocusOut);
    this.addEventListener('keydown', this.handleKeydown);

    // Rail tab switching
    this.railItems.forEach((item) => {
      item.addEventListener('pointerenter', () => this.activate(item.dataset.railTarget));
      item.addEventListener('focus', () => this.activate(item.dataset.railTarget));
      item.addEventListener('click', (e) => {
        e.preventDefault();
        this.activate(item.dataset.railTarget);
      });
    });
  }

  scheduleOpen = () => {
    clearTimeout(this.closeTimer);
    if (this.dataset.open === 'true' || this.openTimer !== null) return;
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.open();
    }, OPEN_DELAY_MS);
  };

  scheduleClose = () => {
    clearTimeout(this.openTimer);
    this.openTimer = null;
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY_MS);
  };

  open = () => {
    clearTimeout(this.openTimer);
    this.openTimer = null;
    this.dataset.open = 'true';
    this.trigger.setAttribute('aria-expanded', 'true');
  };

  close = () => {
    delete this.dataset.open;
    this.trigger.setAttribute('aria-expanded', 'false');
  };

  toggle = () => {
    if (this.dataset.open === 'true') {
      this.close();
    } else {
      this.open();
    }
  };

  activate = (panelId) => {
    if (!panelId) return;
    this.railItems.forEach((item) => {
      const isActive = item.dataset.railTarget === panelId;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', String(isActive));
    });
    this.panels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.railPanel === panelId);
    });
  };

  handleFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) {
      this.close();
    }
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.dataset.open === 'true') {
      this.close();
      this.trigger.focus();
    }
  };
}

if (!customElements.get('header-menu-rail')) {
  customElements.define('header-menu-rail', HeaderMenuRail);
}

/* ============================================================
   <header-menu-rail-mobile> — drill-down stack drawer
   ============================================================ */

class HeaderMenuRailMobile extends HTMLElement {
  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-mobile-toggle]');
    this.drawer = this.querySelector('[data-mobile-drawer]');
    this.closeBtn = this.querySelector('[data-mobile-close]');
    this.panels = Array.from(this.querySelectorAll('[data-panel-id]'));

    if (!this.toggleBtn || !this.drawer || this.panels.length === 0) return;

    // Stack of visited panel IDs (for back navigation)
    this.stack = ['root'];

    this.toggleBtn.addEventListener('click', this.toggleDrawer);
    if (this.closeBtn) this.closeBtn.addEventListener('click', this.closeDrawer);

    // Drill-into buttons
    this.querySelectorAll('[data-drill-into]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.drillInto(btn.dataset.drillInto);
      });
    });

    // Back buttons
    this.querySelectorAll('[data-drill-back]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.drillBack();
      });
    });

    // Escape closes the drawer
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  toggleDrawer = () => {
    if (this.dataset.open === 'true') {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  };

  openDrawer = () => {
    this.dataset.open = 'true';
    this.toggleBtn.setAttribute('aria-expanded', 'true');
    // Reset to root panel each time the drawer is opened
    this.stack = ['root'];
    this.showPanel('root');
  };

  closeDrawer = () => {
    delete this.dataset.open;
    this.toggleBtn.setAttribute('aria-expanded', 'false');
  };

  drillInto = (panelId) => {
    if (!panelId) return;
    const target = this.panels.find((p) => p.dataset.panelId === panelId);
    if (!target) return;

    const currentId = this.stack[this.stack.length - 1];
    this.stack.push(panelId);
    this.transitionPanels(currentId, panelId, 'forward');
  };

  drillBack = () => {
    if (this.stack.length <= 1) return;
    const currentId = this.stack.pop();
    const previousId = this.stack[this.stack.length - 1];
    this.transitionPanels(currentId, previousId, 'backward');
  };

  transitionPanels = (fromId, toId, direction) => {
    const fromPanel = this.panels.find((p) => p.dataset.panelId === fromId);
    const toPanel = this.panels.find((p) => p.dataset.panelId === toId);
    if (!toPanel) return;

    // Position the incoming panel off-screen on the correct side, then animate in
    if (direction === 'forward') {
      // Incoming slides in from right; outgoing slides out left
      toPanel.style.transform = 'translateX(100%)';
      requestAnimationFrame(() => {
        toPanel.style.transform = '';
        toPanel.classList.add('is-active');
        if (fromPanel) {
          fromPanel.classList.remove('is-active');
          fromPanel.classList.add('is-leaving-left');
          setTimeout(() => fromPanel.classList.remove('is-leaving-left'), 300);
        }
      });
    } else {
      // Incoming slides in from left (was already visually offscreen-left); outgoing slides out right
      if (fromPanel) {
        fromPanel.classList.remove('is-active');
        // fromPanel uses default transform: translateX(100%), which slides it out right
      }
      toPanel.classList.remove('is-leaving-left');
      toPanel.style.transform = 'translateX(-100%)';
      requestAnimationFrame(() => {
        toPanel.style.transform = '';
        toPanel.classList.add('is-active');
      });
    }
  };

  showPanel = (panelId) => {
    // Reset all panel state and show only the target
    this.panels.forEach((panel) => {
      panel.classList.remove('is-active', 'is-leaving-left');
      panel.style.transform = '';
    });
    const target = this.panels.find((p) => p.dataset.panelId === panelId);
    if (target) target.classList.add('is-active');
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.dataset.open === 'true') {
      this.closeDrawer();
      this.toggleBtn.focus();
    }
  };
}

if (!customElements.get('header-menu-rail-mobile')) {
  customElements.define('header-menu-rail-mobile', HeaderMenuRailMobile);
}
